import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { Link } from "@quintype/components";
import PropTypes from "prop-types";
import { getCategoryAndSubCategory, getTemplateName } from "../../../ga-utils";

import { SvgIconHandler } from "../../atoms/SvgIconHandler";
import { STATIC_KEYWORDS } from "../../../constants";
import { useDeviceType } from "../../customHooks/useDeviceType";
import ListingAdSlot from "../../molecules/ListingAdSlot";
import { trackLoadMoreClick } from "../../utils";

import "./authors-list.m.css";


export const AuthorsList = ({ data = [] }) => {
  const { AUTHORS, LOAD_MORE, SEARCH, NO_RESULT_FOUND, TRY_AGAIN } = STATIC_KEYWORDS;
  const limit = 15;
  const [searchQuery, setSearchQuery] = useState("");
  const [displayedAuthors, setDisplayedAuthors] = useState(() => data?.authors || []);
  const [currentLimit, setCurrentLimit] = useState(limit);
  const [availableLetters, setAvailableLetters] = useState([]);
  const searchInputRef = useRef(null);
  const isDesktop = useDeviceType() === "desktop";
  const breakPoint = isDesktop ? 9 : 3;
  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const pageType = get(qtState, ["pageType"], {});
  const isAdfreePage = get(qtState, ["config", "subscriber"]) === "true";

  const handleSearch = (e) => {
    e.preventDefault();
    const searchTerm = searchInputRef.current.value;
    if (searchTerm) {
      trackAuthorSearch(searchTerm);
    }
    setSearchQuery(searchTerm);
    setCurrentLimit(limit);
  };

  const trackEvent = (eventAction, extraData = {}) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType);
      window.dataLayer.push({
        event: "dl_author_list_interactions",
        event_action: eventAction,
        ...extraData,
        category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: getTemplateName(pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  const trackAuthorClick = (authorName, index) => {
    trackEvent("author_click", {
      link_text: authorName,
      link_position: index + 1,
    });
  };

  const trackFilterClick = (letter) => {
    trackEvent("filter_applied", {
      link_text: letter,
    });
  };

  const trackAuthorSearch = (searchTerm) => {
    trackEvent("author_search", {
      search_term: searchTerm,
    });
  };

  useEffect(() => {
    if (data?.authors?.length) {
      data.authors.sort((a, b) => a.name.localeCompare(b.name));
      const letters = new Set(data.authors.map((author) => author.name.charAt(0).toUpperCase()));
      setAvailableLetters([...letters]);
    }
  }, [data.authors]);

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "dl_page_info",
      cms_author_name: "NA",
      byline: "NA",
      category: "author",
      sub_category: "NA",
      content_id: "NA",
      template_name: "listing",
      publish_date: "NA",
      publish_time: "NA",
      login_status: "not-logged-in", // will be changed
      subscription_status: "not-subscribed", // will be changed
      ad_free: false, // will be changed
      login_wall_enabled: false,
      platform: window.innerWidth >= 768 ? "Desktop" : "Mobile",
    });
  }, []);

  const allLetters = Array.from("ABCDEFGHIJKLMNOPQRSTUVWXYZ");

  const startsWithCaseInsensitive = (str, prefix) => str.toLowerCase().startsWith(prefix.toLowerCase());

  useEffect(() => {
    let filtered = data.authors || [];
    if (searchQuery) {
      filtered = filtered.filter((author) => startsWithCaseInsensitive(author.name, searchQuery));
    }
    setDisplayedAuthors(filtered.slice(0, currentLimit));
  }, [data.authors, searchQuery, currentLimit]);

  const handleLoadMore = () => {
    trackLoadMoreClick({
      linkCategory: "author",
      category: "author",
      subCategory: "NA",
      templateName: "listing",
    });
    setCurrentLimit((prevLimit) => prevLimit + limit);
  };

  return (
    <div styleName="page-container">
      <div styleName="content-wrapper">
        <div styleName="title-search">
          <h1 styleName="page-title">{AUTHORS}</h1>
          <form onSubmit={handleSearch} styleName="search-container">
            <div styleName="search-wrapper">
              <input
                type="text"
                name="search"
                placeholder="Search Author"
                styleName={`search-input ${searchInputRef?.current?.value !== "" ? "search-input-small" : ""}`}
                ref={searchInputRef}
              />
              {searchQuery && searchInputRef?.current?.value !== "" && (
                <span
                  styleName="clear-search"
                  onClick={() => {
                    setSearchQuery("");
                    searchInputRef.current.value = "";
                  }}
                >
                  <SvgIconHandler type="close" height="12px" width="12px" viewBox="0 0 12 12" />
                </span>
              )}
            </div>
            <button type="submit" styleName="search-button">
              {SEARCH}
            </button>
          </form>
        </div>
        <div styleName="filters-container">
          <div styleName="alphabet-container">
            {allLetters.map((letter) => (
              <button
                key={letter}
                onClick={() => {
                  // Only track if the letter is changing or if it's available
                  if (letter !== searchQuery && availableLetters.includes(letter)) {
                    trackFilterClick(letter);
                  }
                  setSearchQuery(letter === searchQuery ? null : letter);
                  if (searchInputRef?.current) {
                    searchInputRef.current.value = "";
                  }
                }}
                styleName={`letter-button ${letter === searchQuery ? "active" : ""} ${
                  !availableLetters.includes(letter) ? "disabled" : ""
                }`}
                disabled={!availableLetters.includes(letter)}
              >
                {letter}
              </button>
            ))}
          </div>
        </div>
        {displayedAuthors.length < 1 ? (
          <div styleName="oops-text">
            {data?.authors?.length ? (
              <span>
                {NO_RESULT_FOUND}&apos;{searchQuery}&apos; <br /> {TRY_AGAIN}
              </span>
            ) : null}
          </div>
        ) : (
          <div styleName="authors-grid">
            {displayedAuthors.map((author, index) => (
              <>
                <Link
                  href={`/author/${author.slug}`}
                  key={`author-list-${index}`}
                  styleName="author-card"
                  disableAjaxLinks={true}
                  onMouseDown={() => trackAuthorClick(author.name, index)}
                >
                  <div styleName="author-content">
                    <div styleName="avatar-container">
                      {author["avatar-url"] ? (
                        <img src={`${author["avatar-url"]}?w=120&auto=format,compress&fit=max&q=70`} width="80" height="80" loading="lazy" alt={author.name} title={author.name} styleName="avatar-image" />
                      ) : (
                        <SvgIconHandler type="user" width="80px" height="80px" />
                      )}
                    </div>
                    <div styleName="author-info">
                      <h3 styleName="author-name">{author.name}</h3>
                      <p styleName="author-title">{author.metadata?.designation}</p>
                    </div>
                  </div>
                </Link>
                {!isAdfreePage && (index + 1) % breakPoint === 0 && index + 1 < displayedAuthors.length && (
                  <div key={`separator-${index}`} styleName="full-width-separator">
                    <ListingAdSlot
                      adStyleName="ad-slot-size-728x90-300x250"
                      adType="between_rows"
                      index={index}
                      pageType="authors-list"
                    />
                  </div>
                )}
              </>
            ))}
          </div>
        )}
        {!(displayedAuthors.length < currentLimit) && displayedAuthors.length < (data?.authors?.length || 0) && (
          <div styleName="load-more-container">
            <button styleName="load-more-button" onClick={handleLoadMore}>
              {LOAD_MORE}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

AuthorsList.propTypes = {
  data: PropTypes.shape({
    authors: PropTypes.array,
  }),
};
